import { PrintKind, PrintQr, PrintRequest, PrintFromLogRequest } from '../../../models/printing/PrintRequest';
import { ReceivingItemRef } from '../../../models/logs/ReceivingItemRef';
import { singularizeUnitNoun } from './container-items.rules';

/**
 * qr-items.rules.ts
 *
 * Pure rules for building the per-item QR content sent to the print API.
 *
 * Each receiving item row's GUID (returned by the submit response, one per
 * physical item) becomes the `payload` of a finished URL that the QR code
 * encodes: `{appBaseUrl}/view-logged-item?payload={itemGuid}`. The frontend
 * owns URL assembly — the app base URL comes from the Angular environment
 * config so localhost / test / prod resolve automatically — and the print API
 * encodes the finished string verbatim (it never builds URLs, knows
 * environments, or mints GUIDs).
 */

/** The app route a scanned item QR resolves to. */
export const VIEW_LOGGED_ITEM_ROUTE = 'view-logged-item';

/** The query parameter carrying the item GUID on the view-logged-item route. */
export const QR_PAYLOAD_PARAM = 'payload';

/**
 * Builds the finished URL a single item's QR code encodes:
 * `{appBaseUrl}/view-logged-item?payload={itemGuid}`.
 *
 * Throws on a missing base rather than encoding a relative path. A QR code is
 * read by a phone that has no page to resolve one against, so a relative payload
 * produces a sticker that scans successfully and goes nowhere — a failure the
 * warehouse only discovers with the label already on the box. The throw is caught
 * at each call site and degrades to a label with no QR, which is at least
 * visibly wrong.
 *
 * @param appBaseUrl - The environment's app base URL (trailing slash tolerated);
 *   see {@link QR_BASE_URL}, which is where every caller should get it.
 * @param itemGuid - The receiving item row's GUID from the submit response.
 * @returns The finished URL string for the print API to encode verbatim.
 * @throws If `appBaseUrl` is missing or blank.
 */
export function buildQrUrl(appBaseUrl: string, itemGuid: string): string {
  const base = appBaseUrl?.trim().replace(/\/+$/, '');
  if (!base) {
    throw new Error(
      'No app base URL is configured for QR payloads, so this QR code cannot be built. '
      + 'Check qrLogReaderUrl / appUrl in the environment file for this build.',
    );
  }
  return `${base}/${VIEW_LOGGED_ITEM_ROUTE}?${QR_PAYLOAD_PARAM}=${encodeURIComponent(itemGuid)}`;
}

/**
 * Builds the `qr` object for a print request from the log's stored item rows:
 * one `items[]` entry per physical item, each carrying its finished URL and the
 * caption facts the label is printed with.
 *
 * `itemType`/`typeIndex`/`typeTotal` are mapped STRAIGHT THROUGH from the stored
 * rows — never derived from array position. On a mixed delivery the positional
 * fallback would caption the first of two pallets "PALLET 2 of 2", and a partial
 * reprint of one item has no array to count against yet must still say "BOX 2 of
 * 3". Array order only controls the order labels come off the printer.
 *
 * The job-level `itemType` is deliberately omitted: every item carries its own,
 * which is what the print API requires once a job mixes types.
 *
 * Per-item copy counts are left unset here — {@link splitQrPrintRequests} stamps
 * them on.
 *
 * @param appBaseUrl - The environment's app base URL.
 * @param items - The log's item rows (submit response, or the item-ids endpoint).
 * @returns The `qr` request object, or undefined when there are no item rows —
 *   a legacy log that never captured a structured quantity. The print request
 *   then omits `qr` entirely and prints one plain label with no QR code.
 */
export function buildPrintQr(
  appBaseUrl: string,
  items: ReceivingItemRef[],
): PrintQr | undefined {
  const rows = (items ?? []).filter((item) => !!item?.itemId && item.itemId.trim().length > 0);
  if (rows.length === 0) { return undefined; }

  return {
    items: rows.map((item) => ({
      qrUrl: buildQrUrl(appBaseUrl, item.itemId),
      // Every item must resolve to a type or the whole job is a 400, and we no
      // longer send a job-level fallback — so an untyped stored row gets the
      // generic noun rather than killing the print.
      itemType: item.itemType?.trim() || 'item',
      ...numberingOrNothing(item),
    })),
  };
}

/**
 * The `typeIndex`/`typeTotal` pair for one item, or nothing at all.
 *
 * The print API requires them both-or-neither, each 1–999, with index ≤ total.
 * A stored row that fails any of that (an older row predating the columns comes
 * back null) would serialize as a malformed pair and 400 the entire job — a
 * missing caption is the better failure, so the pair is dropped and the API
 * numbers that item positionally.
 */
function numberingOrNothing(item: ReceivingItemRef): { typeIndex?: number; typeTotal?: number } {
  const index = item.typeIndex;
  const total = item.typeTotal;
  const inRange = (n: unknown): n is number => Number.isInteger(n) && (n as number) >= 1 && (n as number) <= 999;
  if (!inRange(index) || !inRange(total) || index > total) { return {}; }
  return { typeIndex: index, typeTotal: total };
}

/**
 * The single-type counterpart of {@link buildPrintQr}, for a caller that only has
 * item GUIDs and one unit noun — today, the desktop log form, which parses its
 * free-text "Quantity Units" field into a single (quantity, type) pair and never
 * mixes types. Positional numbering is correct there, so the job-level `itemType`
 * carries the noun and the API numbers by array position.
 *
 * Mobile must NOT use this: its deliveries can mix types, where position-derived
 * numbering is wrong. Use {@link buildPrintQr} with the stored item rows.
 *
 * @param appBaseUrl - The environment's app base URL.
 * @param itemGuids - Item row GUIDs from the submit response, in item order.
 * @param itemType - The unit noun for the human "TYPE X of N" line (e.g. "box").
 * @returns The `qr` request object, or undefined when there are no item GUIDs.
 */
export function buildSingleTypePrintQr(
  appBaseUrl: string,
  itemGuids: string[],
  itemType: string,
): PrintQr | undefined {
  const guids = (itemGuids ?? []).filter((g) => !!g && g.trim().length > 0);
  if (guids.length === 0) { return undefined; }

  return {
    itemType: itemType?.trim() || undefined,
    items: guids.map((guid) => ({ qrUrl: buildQrUrl(appBaseUrl, guid) })),
  };
}

/**
 * The item rows the SINGLE-TYPE desktop surface prints browser labels from.
 *
 * The browser label path needs full {@link ReceivingItemRef} rows — it prints the
 * caption itself rather than asking the API to compose one — but the desktop form
 * only ever has GUIDs plus one unit noun. This mints the rows that implies, with
 * positional numbering, and carries the same restriction as
 * {@link buildSingleTypePrintQr}: correct ONLY where types cannot mix. Mobile has
 * stored rows and must pass those.
 *
 * @param itemGuids - Item row GUIDs from the submit response, in item order.
 * @param itemType - The unit noun for every item (e.g. "box").
 * @returns One row per GUID, numbered 1..N within the single type.
 */
export function buildSingleTypeItemRefs(
  itemGuids: string[] | undefined,
  itemType: string,
): ReceivingItemRef[] {
  const guids = (itemGuids ?? []).filter((g) => !!g && g.trim().length > 0);
  const noun = itemType?.trim() || 'item';
  return guids.map((itemId, i) => ({
    itemId,
    itemType: noun,
    typeIndex: i + 1,
    typeTotal: guids.length,
  }));
}

/**
 * Splits a QR print request into the per-document requests the print API needs.
 *
 * With per-item QR printing, each item's `copies` value applies to BOTH
 * documents at that index — so a single `kind: 'Both'` request can only carry
 * ONE per-item quantity. When the label and sheet quantities match, one
 * request goes out as-is; when they differ, the request is split into a Zebra
 * request (label copies) and a Copier request (sheet copies).
 *
 * Copies are stamped onto `qr.items[]` explicitly (missing/invalid entries
 * clamp to 1) and the request-level `labelCount`/`copyCount` are cleared on QR
 * requests — per-item copies is the single source of quantity there. A request
 * without a `qr` block (the legacy path) is returned untouched.
 *
 * @param base - The fully-populated print request (kind, printers, text fields,
 *   and a `qr` block without per-item copies).
 * @param labelCopies - Per-item Zebra label copies, by item index.
 * @param sheetCopies - Per-item copier sheet copies, by item index.
 * @returns One or two print requests to submit.
 */
export function splitQrPrintRequests(
  base: PrintRequest,
  labelCopies?: number[],
  sheetCopies?: number[],
): PrintRequest[] {
  return splitQrRequests(base, labelCopies, sheetCopies);
}

/**
 * The from-log counterpart of {@link splitQrPrintRequests}: same split rule, for
 * the intent-only `POST /V1/print/from-log/{logId}` body. Used by the desktop
 * reprint modal, where per-item copy counts ride in the `qr` block and a
 * `Both` request whose label and sheet counts differ becomes two from-log calls.
 *
 * @param base - The from-log request (kind, printers, and a `qr` block without
 *   per-item copies).
 * @param labelCopies - Per-item Zebra label copies, by item index.
 * @param sheetCopies - Per-item copier sheet copies, by item index.
 * @returns One or two from-log requests to submit.
 */
export function splitQrFromLogRequests(
  base: PrintFromLogRequest,
  labelCopies?: number[],
  sheetCopies?: number[],
): PrintFromLogRequest[] {
  return splitQrRequests(base, labelCopies, sheetCopies);
}

/** The shape both print bodies share for the QR copy-count split. */
interface QrSplittable {
  kind: PrintKind;
  qr?: PrintQr;
  labelCount?: number;
  copyCount?: number;
  zebraPrinterName?: string;
  copierPrinterName?: string;
}

/**
 * Shared split core for {@link splitQrPrintRequests} and
 * {@link splitQrFromLogRequests}: stamps per-item copies onto `qr.items[]`,
 * clears the request-level counts, and — for a `Both` request whose label and
 * sheet counts differ — returns one request per document. Preserves the exact
 * request type it was given.
 */
function splitQrRequests<T extends QrSplittable>(
  base: T,
  labelCopies?: number[],
  sheetCopies?: number[],
): T[] {
  if (!base.qr) { return [base]; }

  const itemCount = base.qr.items.length;
  const labels = normalizeCopies(labelCopies, itemCount);
  const sheets = normalizeCopies(sheetCopies, itemCount);

  const stripped = { ...base, labelCount: undefined, copyCount: undefined };
  // Items set to zero copies are dropped from the block rather than sent with a
  // zero the API would reject. The survivors keep their STORED numbering, so a
  // lone reprint still reads "BOX 2 of 3" — the API deliberately does not verify
  // that a type's items sum to its total, which is what makes partial reprint
  // possible at all.
  const qrWith = (copies: number[]): PrintQr => ({
    ...base.qr!,
    items: base.qr!.items
      .map((item, i) => ({ ...item, copies: copies[i] }))
      .filter((item) => item.copies > 0),
  });

  const anyLabels = labels.some((qty) => qty > 0);
  const anySheets = sheets.some((qty) => qty > 0);

  if (base.kind === 'Zebra') { return anyLabels ? [{ ...stripped, qr: qrWith(labels) }] : []; }
  if (base.kind === 'Copier') { return anySheets ? [{ ...stripped, qr: qrWith(sheets) }] : []; }

  // Both: one request when every item's label and sheet quantities agree,
  // otherwise one request per document — and no request at all for a document
  // whose every item was zeroed out.
  if (anyLabels && anySheets && labels.every((qty, i) => qty === sheets[i])) {
    return [{ ...stripped, qr: qrWith(labels) }];
  }
  const requests: T[] = [];
  if (anyLabels) {
    requests.push({ ...stripped, kind: 'Zebra' as PrintKind, copierPrinterName: undefined, qr: qrWith(labels) });
  }
  if (anySheets) {
    requests.push({ ...stripped, kind: 'Copier' as PrintKind, zebraPrinterName: undefined, qr: qrWith(sheets) });
  }
  return requests;
}

/**
 * Normalizes a per-item copies array to the item count: missing entries default
 * to 1, and every value is floored to a whole number of at least 0.
 *
 * Zero means "don't print this one", and the caller drops those items from the
 * QR block — the Print API still requires ≥1 for every item it is SENT. That is
 * how an operator reprints only the label they actually lost instead of the
 * whole pallet, which matters most when a printer outage has made reprint the
 * primary label path for a shift. Numbering is unaffected: each item carries its
 * own stored "X of N", so a lone survivor still prints "BOX 2 of 3".
 *
 * A missing or unusable entry defaults to 1 rather than 0 — dropping an item
 * because a value was undefined would silently under-print.
 *
 * Exported because the browser label path clamps the same way: both paths have to
 * turn the same quantities into the same number of labels, or a printer outage
 * would change how much gets printed as well as where.
 */
export function normalizeCopies(copies: number[] | undefined, itemCount: number): number[] {
  return Array.from({ length: itemCount }, (_, i) => {
    const raw = copies?.[i];
    if (raw === undefined || raw === null) { return 1; }
    const value = Math.floor(raw);
    return Number.isFinite(value) && value >= 0 ? value : 1;
  });
}

/** Discrete item facts parsed from a free-text quantity-units string. */
export interface ParsedItemFacts {
  /** Number of physical items in the delivery (1–99). */
  quantity: number;
  /** Singular unit noun for the items (e.g. "box"); 'item' when none was given. */
  itemType: string;
}

/**
 * Parses the desktop form's free-text "Quantity Units" value (e.g. "3 boxes")
 * into the discrete quantity + unit noun the item-tracking submit fields need.
 * (The mobile flow captures these discretely and never parses.)
 *
 * Accepted shapes: "3 boxes" / "3" (noun defaults to 'item') / "crate" (no
 * leading count → a single unit of that noun). Returns null — meaning "create
 * no item rows", the legacy path — for blank input, a leading zero, or a
 * leading number of 3+ digits, which is treated as a measurement rather than
 * a unit count ("500 ft of pipe" is one delivery, not 500 items).
 *
 * @param qtyUnits - The free-text quantity-units value from the form.
 * @returns The parsed facts, or null when no item rows should be created.
 */
export function parseItemFacts(qtyUnits: string | null | undefined): ParsedItemFacts | null {
  const text = (qtyUnits ?? '').trim();
  if (!text) { return null; }

  const match = text.match(/^(\d{1,2})(?!\d)\s*(.*)$/);
  if (match) {
    const quantity = parseInt(match[1], 10);
    if (quantity < 1) { return null; }
    return { quantity, itemType: cleanUnitNoun(match[2]) };
  }

  // Leading 3+ digit number: a measurement, not a unit count.
  if (/^\d/.test(text)) { return null; }

  return { quantity: 1, itemType: cleanUnitNoun(text) };
}

/**
 * Normalizes a raw unit noun: trims, drops a leading multiplier "x" ("3 x
 * boxes"), lowercases, and singularizes the final word so labels read
 * "BOX 1 of 3" rather than "BOXES 1 of 3". Falls back to 'item' when blank.
 */
function cleanUnitNoun(raw: string): string {
  const noun = raw.trim().replace(/^[xX]\s+/, '').toLowerCase();
  if (!noun) { return 'item'; }

  const words = noun.split(/\s+/);
  words[words.length - 1] = singularizeUnitNoun(words[words.length - 1]);
  return words.join(' ');
}

